import {
  collectAttachmentEntries,
  resolveAttachmentIds,
  resolveAttachmentKey,
} from '../../../../utils/informationManagerComponentUtils';
import { INFORMATION_MANAGER_TYPE_OPTIONS } from '../../../../utils/constants';

export const ATTACHMENT_FIELD_ORDER = ['fileid', '_id', 'file_id', 'id', 'uploadid', 'upload_id'];

/**
 * Normalise legacy response types to the three supported values.
 * @param {string} type
 * @returns {'fileupload'|'text'|'link'|string}
 */
export function normalizeRequestType(type) {
  if (type === 'fileuploadtolink') return 'link';
  return type || '';
}

/**
 * Human-readable label for a response type.
 * @param {string} type
 * @returns {string}
 */
export function resolveResponseTypeLabel(type) {
  const normalized = normalizeRequestType(type);
  const match = INFORMATION_MANAGER_TYPE_OPTIONS.find((option) => option.value === normalized);
  return match?.name || normalized || '—';
}

/**
 * Collect downloadable attachments from a request row.
 * @param {Object} request
 * @returns {Array<Object>}
 */
export function getRequestAttachments(request = {}) {
  return collectAttachmentEntries(request);
}

/**
 * Whether download actions should be shown for a request.
 * Link responses have no files. Text/fileupload only when attachments exist.
 * @param {Object} request
 * @returns {boolean}
 */
export function canDownloadAttachments(request = {}) {
  if (normalizeRequestType(request?.type) === 'link') return false;
  const files = getRequestAttachments(request);
  const requestId = request?._id != null ? String(request._id) : '';
  return files.some((file) => {
    const ids = resolveAttachmentIds(file, ATTACHMENT_FIELD_ORDER)
      .map((id) => String(id == null ? '' : id).trim())
      .filter((id) => id && id !== requestId && id !== 'undefined' && id !== 'null');
    return ids.length > 0;
  });
}

/**
 * Download a single attachment or all ids for a request.
 * @param {Object} nova - Vue $nova helper
 * @param {Object} file - Attachment object
 */
export function downloadAttachment(nova, file) {
  if (!nova) return;
  const candidateIds = resolveAttachmentIds(file, ATTACHMENT_FIELD_ORDER);
  if (candidateIds.length === 1) {
    nova.downloadFile(candidateIds[0]);
  } else if (candidateIds.length > 1 && typeof nova.downloadFiles === 'function') {
    nova.downloadFiles(candidateIds);
  }
}

/**
 * Download all attachments for a request.
 * @param {Object} nova
 * @param {Object} request
 */
export function downloadAllAttachments(nova, request) {
  if (!nova || !canDownloadAttachments(request)) return;
  const files = getRequestAttachments(request);
  const ids = files.reduce((acc, file) => {
    const resolved = resolveAttachmentIds(file, ATTACHMENT_FIELD_ORDER);
    if (resolved.length > 0) acc.push(...resolved);
    return acc;
  }, []);
  if (ids.length === 0) return;
  if (typeof nova.downloadFiles === 'function') {
    nova.downloadFiles(ids);
  } else if (ids.length === 1) {
    nova.downloadFile(ids[0]);
  }
}

/**
 * Resolve attachment id list from retained attachment objects.
 * @param {Array<Object>} attachments
 * @returns {Array<string|number>}
 */
export function resolveRetainedFileIds(attachments = []) {
  return (Array.isArray(attachments) ? attachments : []).reduce((acc, file) => {
    const resolved = resolveAttachmentIds(file, ATTACHMENT_FIELD_ORDER);
    if (resolved.length > 0) acc.push(...resolved);
    return acc;
  }, []);
}

/**
 * Build updateResponse payload for save / submit-on-behalf.
 * @param {Object} request
 * @param {{ response?: string, remark?: string, files?: File[], retainedAttachments?: Object[] }} form
 * @param {{ submit?: boolean }} options
 * @returns {{ data: Object, files: File[] }}
 */
export function buildResponseSubmitPayload(request = {}, form = {}, options = {}) {
  const type = normalizeRequestType(request.type);
  const remark = (form.remark || '').trim();
  const submit = options.submit !== false;
  const data = { remark, submit };

  if (type === 'text') {
    data.response = (form.response || '').trim();
    data.fileid = resolveRetainedFileIds(form.retainedAttachments);
    return {
      data,
      files: Array.isArray(form.files) ? form.files.filter(Boolean) : [],
    };
  }

  if (type === 'link') {
    return { data, files: [] };
  }

  const retainedIds = resolveRetainedFileIds(form.retainedAttachments);
  data.fileid = retainedIds;

  return {
    data,
    files: Array.isArray(form.files) ? form.files.filter(Boolean) : [],
  };
}

/**
 * Validate save / submit-on-behalf form before dispatching updateResponse.
 * Save (submit: false) allows partial drafts. Submit rules by type:
 * - text: response required; remark/files optional
 * - fileupload: at least one file required; remark optional
 * - link: remark optional only (no files)
 * @param {Object} request
 * @param {{ response?: string, remark?: string, files?: File[], retainedAttachments?: Object[] }} form
 * @param {{ submit?: boolean }} options
 * @returns {string|null} Error message or null when valid
 */
export function validateResponseSubmit(request = {}, form = {}, options = {}) {
  if (options.submit === false) return null;

  const type = normalizeRequestType(request.type);

  if (type === 'fileupload') {
    const newFiles = Array.isArray(form.files) ? form.files.filter(Boolean) : [];
    const retained = Array.isArray(form.retainedAttachments)
      ? form.retainedAttachments.filter(Boolean)
      : [];
    if (newFiles.length === 0 && retained.length === 0) {
      return 'Please upload at least one file';
    }
    return null;
  }

  if (type === 'text') {
    if (!(form.response || '').trim()) return 'Please provide a response';
    return null;
  }

  // link — remark optional, nothing required
  return null;
}

export { resolveAttachmentKey };

const ACCRUALS_PO_FIELDS = [
  { key: 'po_number', label: 'PO number' },
  { key: 'po_line_number', label: 'PO line' },
  { key: 'po_status', label: 'PO status' },
  { key: 'po_currency', label: 'Currency' },
  { key: 'open_po_value', label: 'Open PO value', type: 'money' },
  { key: 'total_po_value', label: 'Total PO value', type: 'money' },
  { key: 'cost_center', label: 'Cost center' },
  { key: 'subsidiary_code', label: 'Subsidiary code' },
  { key: 'subsidiary', label: 'Subsidiary' },
];

const ACCRUALS_VENDOR_FIELDS = [
  { key: 'supplier_name', label: 'Supplier name' },
  { key: 'supplier_id', label: 'Supplier ID' },
];

const ACCRUALS_AP_FIELDS = [
  { key: 'invoice_number', label: 'Invoice number' },
  { key: 'transaction_id', label: 'Transaction ID' },
  { key: 'invoice_amount', label: 'Amount', type: 'money' },
  { key: 'transaction_currency', label: 'Currency' },
  { key: 'transaction_date', label: 'Transaction date', type: 'date' },
  { key: 'invoice_status', label: 'Status' },
  { key: 'po_number', label: 'PO number' },
  { key: 'supplier_id', label: 'Supplier ID' },
];

const ACCRUALS_OPEN_INVOICE_FIELDS = [
  { key: 'invoice_number', label: 'Invoice number' },
  { key: 'invoice_id', label: 'Invoice ID' },
  { key: 'invoice_amount', label: 'Amount', type: 'money' },
  { key: 'invoice_date', label: 'Invoice date', type: 'date' },
  { key: 'invoice_status', label: 'Status' },
  { key: 'po_number', label: 'PO number' },
  { key: 'supplier_id', label: 'Supplier ID' },
];

/**
 * Parse accruals_description when stored as a JSON string.
 * @param {*} raw
 * @returns {Object|null}
 */
function parseAccrualsDescriptionRaw(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (err) {
      return null;
    }
  }
  if (typeof raw === 'object') return raw;
  return null;
}

/**
 * Resolve the cycle entry from accruals_description.
 * Supports cycle-keyed maps and already-unwrapped entries.
 * @param {*} raw
 * @param {string} [cycleId]
 * @returns {Object|null}
 */
export function resolveAccrualsDescriptionEntry(raw, cycleId = '') {
  const parsed = parseAccrualsDescriptionRaw(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  if (parsed.po || parsed.vendor || parsed.ap || parsed.open_invoices || parsed.request_category) {
    return parsed;
  }

  const key = cycleId ? String(cycleId) : '';
  if (key && parsed[key] && typeof parsed[key] === 'object') {
    return parsed[key];
  }

  const keys = Object.keys(parsed);
  if (!keys.length) return null;
  const first = parsed[keys[0]];
  return first && typeof first === 'object' && !Array.isArray(first) ? first : null;
}

/**
 * @param {*} value
 * @returns {boolean}
 */
function hasDisplayValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * @param {*} value
 * @param {'text'|'money'|'date'} [type]
 * @returns {string}
 */
export function formatAccrualsFieldValue(value, type = 'text') {
  if (!hasDisplayValue(value)) return '—';
  if (type === 'money') {
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  }
  if (type === 'date') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return String(value);
}

/**
 * @param {Object} source
 * @param {Array<{ key: string, label: string, type?: string }>} fieldDefs
 * @returns {Array<{ key: string, label: string, value: string }>}
 */
function buildAccrualsFields(source, fieldDefs) {
  const data = source && typeof source === 'object' ? source : {};
  return fieldDefs
    .filter((field) => hasDisplayValue(data[field.key]))
    .map((field) => ({
      key: field.key,
      label: field.label,
      value: formatAccrualsFieldValue(data[field.key], field.type || 'text'),
    }));
}

/**
 * @param {Array<Object>} rows
 * @param {Array<{ key: string, label: string, type?: string }>} fieldDefs
 * @returns {Array<{ key: string, fields: Array<{ key: string, label: string, value: string }> }>}
 */
function buildAccrualsRowSections(rows, fieldDefs) {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows
    .map((row, index) => {
      const fields = buildAccrualsFields(row, fieldDefs);
      if (!fields.length) return null;
      return { key: `row-${index}`, fields };
    })
    .filter(Boolean);
}

/**
 * Build display model for Accruals description on IR detail.
 * @param {*} raw - request.accruals_description
 * @param {string} [cycleId]
 * @returns {{
 *   hasContent: boolean,
 *   poFields: Array<{ key: string, label: string, value: string }>,
 *   vendorFields: Array<{ key: string, label: string, value: string }>,
 *   apRows: Array<{ key: string, fields: Array<{ key: string, label: string, value: string }> }>,
 *   openInvoiceRows: Array<{ key: string, fields: Array<{ key: string, label: string, value: string }> }>
 * }}
 */
export function buildAccrualsDescriptionView(raw, cycleId = '') {
  const entry = resolveAccrualsDescriptionEntry(raw, cycleId);
  if (!entry) {
    return {
      hasContent: false,
      poFields: [],
      vendorFields: [],
      apRows: [],
      openInvoiceRows: [],
    };
  }

  const poFields = buildAccrualsFields(entry.po, ACCRUALS_PO_FIELDS);
  const vendorFields = buildAccrualsFields(entry.vendor, ACCRUALS_VENDOR_FIELDS);
  const apRows = buildAccrualsRowSections(entry.ap, ACCRUALS_AP_FIELDS);
  const openInvoiceRows = buildAccrualsRowSections(entry.open_invoices, ACCRUALS_OPEN_INVOICE_FIELDS);

  return {
    hasContent: Boolean(
      poFields.length || vendorFields.length || apRows.length || openInvoiceRows.length,
    ),
    poFields,
    vendorFields,
    apRows,
    openInvoiceRows,
  };
}
